import { useMemo } from 'react';
import { useJiraWorkmanagementProjectNavigation } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/common/project-sidebar-navigation-resource/index.tsx';
import { useHasActiveSoftwareAccess } from '@atlassian/jira-navigation-apps-sidebar-common/src/controllers/has-active-software-access/index.tsx';
import { useIsPremiumUserSeat } from '@atlassian/jira-router-resources-business-license-details/src/index.tsx';
import { useApplicationPermissions } from '@atlassian/jira-tenant-context-controller/src/components/application-permissions/index.tsx';
import type { RouteResourceError } from '@atlassian/react-resource-router';
import type { Features, ReportFeatures, SettingFeatures } from '../../common/types/index.tsx';
import { getExtraData, getExtraSettingsData, getExtraReportData } from './util/index.tsx';

type UseFeaturesReturn = {
	data: Features | null;
	loading: boolean;
	error: RouteResourceError | null;
};

type UseSettingFeaturesReturn = {
	data: SettingFeatures | null;
	loading: boolean;
	error: RouteResourceError | null;
};

type UseReportFeaturesReturn = {
	data: ReportFeatures | null;
	loading: boolean;
	error: RouteResourceError | null;
};

export const useFeatures = (): UseFeaturesReturn => {
	const { data, loading, error } = useJiraWorkmanagementProjectNavigation();

	const isPremiumUserSeat = useIsPremiumUserSeat();
	const transformedData = useMemo(
		() => data && getExtraData(data, isPremiumUserSeat),
		[data, isPremiumUserSeat],
	);
	return { data: transformedData, loading, error };
};

export const useSettingFeatures = (): UseSettingFeaturesReturn => {
	const hasActiveSoftwareAccess = useHasActiveSoftwareAccess();
	const { data, loading, error } = useJiraWorkmanagementProjectNavigation();
	const transformedData = useMemo(
		() => data && getExtraSettingsData(data, hasActiveSoftwareAccess),
		[data, hasActiveSoftwareAccess],
	);
	return { data: transformedData, loading, error };
};

export const useReportFeatures = (): UseReportFeaturesReturn => {
	const { data, loading, error } = useJiraWorkmanagementProjectNavigation();
	const { hasSoftwareAccess } = useApplicationPermissions();
	const transformedData = useMemo(
		() => data && getExtraReportData(hasSoftwareAccess),
		[data, hasSoftwareAccess],
	);
	return { data: transformedData, loading, error };
};
